/**
 * Step 1 — Item Definition (workflow v2, TypeScript).
 *
 * Three render paths driven by the reducer status:
 *
 *   1. failed                       — error banner
 *   2. empty / generating / revising / loading → in-flight card
 *   3. draft / approved (with structured fetch loaded) → 12-section
 *      detail view (driven by `STEP1_SECTIONS` registry)
 *
 * Props shape: `Step1BodyProps` — grouped into `status`, `result`,
 * `importedChildTaras`, `edit`, `structured`. The `useStep1ItemDefinition`
 * hook composes the underlying sub-flows and produces this exact
 * shape, ready to spread.
 *
 * Sections render via the registry — adding/removing a section is
 * one edit in `step1-sections/registry.ts`, not in this file.
 *
 * Pure renderer: no API calls, no `useState` for data — the hook
 * owns all async lifecycle.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './Step1ItemDefinition.module.css';
import type { Step1BodyProps } from './stepBodyProps';
import { STEP1_SECTIONS } from './step1-sections/registry';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import TableTabNav from '../components/TableTabNav/TableTabNav';

import { Icon } from '../../../components/ui/Icon';
import { Button } from '../../../components/ui/Button2';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

export default function Step1ItemDefinition({
  status,
  result,
  importedChildTaras,
  editable,
  edit,
  structured,
  errorMessage,
}: Step1BodyProps) {
  // NOTE: every hook must be called unconditionally and in the same
  // order on every render. The early returns below (failed / spinner
  // / no-data states) come AFTER the hook block so React's hook
  // count stays stable across `status` transitions
  // (draft → revising → draft would otherwise change the hook count
  // and trigger React's "Expected static flag" warning).

  // Render context — always computed even when we'll bail to a
  // failed/spinner branch below. Cheap and pure.
  const data = edit.isEditing && edit.draft ? edit.draft : structured.data;
  const rawAgent = { imported_child_taras: importedChildTaras, ...(result as any ?? {}) };
  const ctx = { data, rawAgent, isEditing: edit.isEditing, onChange: edit.onChange };

  // Filter to visible sections only (e.g. LinkedSubTaras hides in
  // individual-mode), then drive a horizontal tab selector.
  const visibleSections = useMemo(
    () => STEP1_SECTIONS.filter((s) => !s.visible || s.visible(ctx)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawAgent],
  );

  // URL ↔ activeKey sync (`?section=key`). Seed once on mount, then
  // write `?section=` on every change so refresh / deep-link works.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSection = (() => {
    const fromUrl = searchParams.get('section');
    if (fromUrl && visibleSections.some((s) => s.key === fromUrl)) return fromUrl;
    return visibleSections[0]?.key ?? '';
  })();
  const [activeKey, setActiveKey] = useState<string>(initialSection);
  const seededRef = useRef(false);
  useEffect(() => {
    seededRef.current = true;
  }, []);
  useEffect(() => {
    if (!seededRef.current) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!activeKey) {
          next.delete('section');
          return next;
        }
        if (next.get('section') === activeKey) return prev;
        next.set('section', activeKey);
        return next;
      },
      { replace: true },
    );
  }, [activeKey, setSearchParams]);

  // ─── Early-return branches (all hooks above; React's hook count
  //     stays constant as `status` changes) ─────────────────────────

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Analysis Failed"
        description={
          structured.error ??
          'The analysis agent encountered an error processing your document.'
        }
      />
    );
  }

  // No structured payload yet — show in-flight hero. Covers `empty`
  // (agent hasn't started — brief), `generating` (first run), and
  // `revising` (re-run after revise). The structured fetch loading
  // state also lands here so the swap from "agent done" to "data
  // visible" feels smooth.
  if (status === 'empty' || status === 'generating' || status === 'revising' || structured.loading) {
    return (
      <LoadingStateAny
        variant="page"
        title="Generating Item Definition"
        description="The analysis agent is processing your document. This usually takes 10-30 minutes."
      />
    );
  }

  if (!structured.data) {
    // draft/approved without data — usually a transient state right
    // after the structured fetch failed. Surface the error.
    return (
      <FailedStateAny
        variant="page"
        title="Analysis Failed"
        description={errorMessage ?? structured.error ?? 'Item definition unavailable.'}
      />
    );
  }

  // ─── Full render path ─────────────────────────────────────────────

  const active =
    visibleSections.find((s) => s.key === activeKey) ?? visibleSections[0];

  // Disabled (not hidden) while the workflow is mid-run and Step 1 is no
  // longer the current step — the backend would 403 the save. The title
  // explains why, which the raw "Access denied" toast never did.
  const editActions = !edit.isEditing ? (
    <ButtonAny
      variant="outline"
      size="sm"
      startIcon={<Icon name="edit" size={14} />}
      onClick={edit.onToggle}
      disabled={!editable}
      title={
        editable
          ? undefined
          : 'Editing is locked while the analysis is running. It unlocks when the analysis completes.'
      }
    >
      Edit
    </ButtonAny>
  ) : (
    <>
      <ButtonAny variant="outline" size="sm" onClick={edit.onCancel} disabled={edit.saving}>
        Cancel
      </ButtonAny>
      <ButtonAny variant="default" size="sm" loading={edit.saving} onClick={edit.onSave}>
        {edit.saving ? 'Saving…' : 'Save Changes'}
      </ButtonAny>
    </>
  );

  return (
    <>
      <TableTabNav
        tabs={visibleSections.map((s) => ({ key: s.key, label: s.label }))}
        activeKey={active?.key ?? null}
        onChange={setActiveKey}
        right={editActions}
        ariaLabel="Item definition sections"
      />

      {edit.error && <div className={styles.errorBanner}>{edit.error}</div>}

      {active && active.render(ctx)}
    </>
  );
}
